import { FC, useEffect, useState } from 'react';
import { ErrorResponse, HashRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { appInsights } from './AppInsights';
import EnrollmentChecker from './Components/TermsOfUse/EnrollmentChecker';
import ConfirmContextProvider from './Contexts/ConfirmContextProvider';
import { useNotification } from './Contexts/NotificationContext';
import { useUserContext } from './Contexts/UserContext';
import { ActionTypes, ClientConfiguration, Conversation, ConversationCreate, ConversationPatch, GptTypes, IGptConfiguration } from './EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useEnterpriseGPTApiCall } from './Hooks/useMiddlewareApiCall';
import { getInitialConversationPageSize } from './Hooks/useInfiniteConversations';
import { PageAdminSettings } from './Page/PageAdminSettings';
import { PageChat } from './Page/PageChat';
import { PageConfigureGpt } from './Page/PageConfigureGpt';
import { PageError } from './Page/PageError';
import { PageExploreGpts } from './Page/PageExploreGpts';
import { PageShowGptSummary } from './Page/PageShowGptSummary';
import { PageLayout } from './Page/PageLayout';
import { PageSettings } from './Page/PageSettings';
import { 
    AdminSettingsRoute, 
    ChatRoute, 
    ChatSpecificRoute, 
    ErrorRoute, 
    ExploreGptRoute, 
    ExploreGptSummaryRoute, 
    PersonalGptRoute, 
    PersonalGptSpecificRoute, 
    SettingsRoute,
    SettingsConversationsRoute,
    SettingsUserManagementRoute,
    SettingsSurveysRoute,
    SettingsSurveysNewRoute,
    SettingsSurveysEditRoute,
    SettingsSurveysViewRoute
} from './Routes';
import { logout } from './Security/azureAdAuthentication';
import { setConversationsAction } from './store/actions/conversationActions';
import { setFeaturesAction } from './store/actions/featureActions';
import { setSurveyAction } from './store/actions/surveyAction';
import { setUserPreferencesAction } from './store/actions/userPreferencesAction';
import { useRootState } from './store/hooks';
import { EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_StartUp, UserInteractionEventProperties } from './store/models/customEvents';
import { isPWA } from './Utils/browser';
import { getUserInfoDebug } from './Utils/personaUtils';
import { loadGptConfigurations } from './Utils/gptConfigurationLoader';

export const Router: FC = () => {
    const { dispatch, state } = useRootState();
    const { dispatchNotification } = useNotification();
    const { userProfile } = useUserContext();
    const [conversationNotFoundError, setConversationNotFoundError] = useState(false);
    
    useEnterpriseGPTApiCall(async (client, abortSignal) => {
        let clientConfiguration: ClientConfiguration | null = null;
        
        try {
            // signal app-level loading start
            dispatch({ type: 'SET_APP_LOADING', payload: true } as any);
            clientConfiguration = await client.getClientConfigurationsOptions(abortSignal);
            console.log('clientConfiguration:', clientConfiguration);
            appInsights.trackTrace({ 
                message: 'clientConfiguration loaded; ' + getUserInfoDebug(userProfile),
                properties: clientConfiguration });

            dispatch(setFeaturesAction(clientConfiguration));
        } catch (e: unknown) {
            if ((e as Error).message !== 'canceled') {
                dispatchNotification('error.api.get.clientconfigurations', 'error');
            }
        }

        try {
            const survey = await client.getActiveSurveyForUser(abortSignal);
            // console.log('survey:', survey?.id);
            dispatch(setSurveyAction(survey));
        } catch (e) {
            dispatchNotification('error.api.get.survey', 'error');
        }

        try {
            dispatch({ type: 'SET_CONVERSATIONS_LOADING', payload: true } as any);
            const getuserPreferences = await client.getUserPreferences(abortSignal);
            // use this piece of code, in case you want to override the limited user preferences also on the DB

            // // Keep at most 4 GPTs (+ SyGPT) pinned
            // if (getuserPreferences.addedGpts?.length! >= 4) {
            //     // pick the last 3 added GPTs
            //     getuserPreferences.addedGpts = getuserPreferences.addedGpts?.slice(-3);
            // }
            dispatch(setUserPreferencesAction(getuserPreferences));

            // Load GPT configurations
            await loadGptConfigurations(client, dispatch, clientConfiguration, userProfile ?? null, abortSignal);

            // Load initial conversations using getConversationsPaged with fullLoad = true
            // This loads the first page of conversations (limit will be determined by backend default)
            const conversationsPage = await client.getConversationsPaged(
                undefined, // projectId = null
                undefined, // no cursor (first page)
                getInitialConversationPageSize(),        // load first X conversations
                false,     // fullLoad = false (incremental loading)
                abortSignal
            );
            let conversations = conversationsPage.conversations ?? [];

            // Check if there's a conversation ID in the URL (e.g., #/chat/65b0d068-70fe-4dbd-b8cc-437963b306fb)
            const hash = window.location.hash;
            const chatRouteMatch = hash.match(/^#\/chat\/([a-f0-9-]{36})$/i);
            const urlConversationId = chatRouteMatch ? chatRouteMatch[1] : null;
            
            // If URL contains a specific conversation ID, load it
            if (urlConversationId) {
                try {
                    const urlConversation = await client.getConversationById(urlConversationId, abortSignal);
                    if (urlConversation) {
                        // Mark as fully loaded
                        (urlConversation as any).fullyLoaded = true;
                        // Check if this conversation is already in the list
                        const existingIndex = conversations.findIndex(c => c.id === urlConversationId);
                        if (existingIndex >= 0) {
                            // Replace the summary with full conversation
                            conversations[existingIndex] = urlConversation;
                        } else {
                            // Add it at the beginning if not in the list
                            conversations = [urlConversation, ...conversations];
                        }
                    } else {
                        // Conversation not found - show error notification
                        setConversationNotFoundError(true);
                    }
                } catch (err) {
                    console.warn('Failed to load conversation from URL:', urlConversationId, err);
                    // Show error notification when conversation doesn't exist
                    setConversationNotFoundError(true);
                    // Fall through to default behavior
                }
            } else {
                // Auto-load the most recent conversation (default behavior when no URL ID)
                if (conversations.length > 0) {
                    const fullConversation = await client.getConversationById(conversations[0].id, abortSignal);
                    if (fullConversation) {
                        // Mark as fully loaded
                        (fullConversation as any).fullyLoaded = true;
                        conversations[0] = fullConversation;
                    }
                }
            }

            // create an empty draft conversation, if not available, only for SyGPT
            const syGptConversations = conversations.filter(c => c.gpt?.type === GptTypes.SyGpt)
            const syGptLastConversation = syGptConversations.at(0);
            if (!syGptLastConversation || (syGptLastConversation?.prompts && syGptLastConversation.prompts.length > 0)) {
                const gpt = state.gptConfigurations.filter((g: IGptConfiguration) => g.type === GptTypes.SyGpt).at(0);
                // Create a draft conversation locally instead of calling the backend
                const draftConversation = new Conversation({
                    id: 'DRAFT',
                    gpt: { id: gpt?.id, type: GptTypes.SyGpt },
                    prompts: [],
                    title: '',
                    currentQuery: '',
                    startedOn: new Date(),
                    userName: userProfile?.username || '',
                    llm: ''
                });
                // Insert at position 0 since it's the most recent conversation
                conversations = [draftConversation, ...conversations];
            }

            const initialConversations = conversations.map((c) => ({ ...c, active: false, draft: c.id === 'DRAFT', prompts: c.prompts?.map((p) => ({ ...p, aborted: p.answer === null })) }));
            //console.log('Router.tsx - Initial conversations loaded:', initialConversations.length, 'nextCursor:', conversationsPage.nextCursor);
            dispatch({ type: 'SET_CONVERSATIONS_LOADING', payload: false } as any);
            dispatch(setConversationsAction(initialConversations, conversationsPage.nextCursor ?? undefined));
            dispatch({ type: 'SET_APP_LOADING', payload: false } as any);
        } catch (e: unknown) {
            dispatch({ type: 'SET_CONVERSATIONS_LOADING', payload: false } as any);
            dispatch({ type: 'SET_APP_LOADING', payload: false } as any);
            if ((e as ErrorResponse).status === 401) {
                dispatchNotification('error.api.common.expiredAuthentication', 'error', 'Login', logout);
            }
            else if ((e as Error).message !== 'canceled') {
                dispatchNotification('error.api.get.conversations', 'error');
            }
        }
    });

    useEffect(() => {
        if (userProfile) {
            const properties: UserInteractionEventProperties = {
                applicationType: EventProperty_Value_ApplicationType,
                userId: userProfile?.username,
                interactionType: EventProperty_Value_StartUp,
                interactionValue: isPWA() ? 'pwa' : 'browser'
            };

            appInsights.trackEvent({ name: EventName_UserInteraction, properties });
        }
    }, [userProfile]);

    // Show notification after component is fully mounted if conversation was not found
    // Use setTimeout to ensure the notification system is fully ready (after enrollment check, etc.)
    useEffect(() => {
        if (conversationNotFoundError) {
            const timer = setTimeout(() => {
                dispatchNotification('error.api.get.conversation', 'error');
                setConversationNotFoundError(false);
            }, 1000); // Wait 1 second to ensure UI is fully loaded
            
            return () => clearTimeout(timer);
        }
    }, [conversationNotFoundError, dispatchNotification]);

    // The EnrollmentChecker component is used to check if the user has accepted the terms of use
    return <>
        <HashRouter>
            <Routes>
                {/* Error page route - standalone without layout */}
                <Route path={ErrorRoute} element={<PageError />} />
                
                {/* Main app routes with layout */}
                <Route
                    element={
                        <EnrollmentChecker>
                            <ConfirmContextProvider>
                                <PageLayout>
                                    <Outlet />
                                </PageLayout>
                            </ConfirmContextProvider>
                        </EnrollmentChecker>
                    }
                >
                    <Route path={ChatRoute} element={<PageChat />} />
                    <Route path={ExploreGptRoute} element={<PageExploreGpts />} />
                    <Route path={ExploreGptSummaryRoute} element={<PageShowGptSummary />} />
                    <Route path={PersonalGptRoute} element={<PageConfigureGpt />} />
                    <Route path={PersonalGptSpecificRoute} element={<PageConfigureGpt />} />

                    <Route path={ChatSpecificRoute} element={<PageChat />} />
                    
                    {/* Settings routes */}
                    <Route path={SettingsConversationsRoute} element={<PageSettings />} />
                    <Route path={SettingsUserManagementRoute} element={<PageSettings />} />
                    <Route path={SettingsSurveysRoute} element={<PageSettings />} />
                    <Route path={SettingsSurveysNewRoute} element={<PageSettings />} />
                    <Route path={SettingsSurveysEditRoute} element={<PageSettings />} />
                    <Route path={SettingsSurveysViewRoute} element={<PageSettings />} />
                    <Route path={SettingsRoute} element={<Navigate to={SettingsConversationsRoute} replace />} />
                    
                    <Route path={AdminSettingsRoute} element={<PageAdminSettings />} />
                    <Route path='*' element={<Navigate to={ChatRoute} replace />} />
                </Route>
            </Routes>
        </HashRouter>
    </>;
};
